// DocumentUploadField.tsx — a small, reusable "attach a file, get back a
// patient_documents id" control (Insurance Claims Phase 4.5.4/4.5.6, for the
// authorisation decision letter and the settlement advice upload). No
// reusable upload component exists elsewhere in this codebase (every flow
// hand-rolls its own file input against `uploadPatientDocument`, which is
// patient-scoped — see that function's own header in src/api.ts) — a claim
// always has a patient/uhid in context, so this wraps it rather than
// inventing a second upload path.
import { useState, useRef } from 'react';
import { uploadPatientDocument } from '@/api';
import { Button } from '@/components/ui/Button';

export interface DocumentUploadFieldProps {
  uhid: string;
  /** One of patient_documents.doc_category_check's allowed values. */
  category: string;
  /** Called with the newly created patient_documents row's id. */
  onUploaded: (documentId: number) => void;
  uploadedFileName?: string | null;
  disabled?: boolean;
  /** Extra classes for the Attach button (e.g. an accent outline in a checklist row). */
  className?: string;
}

export function DocumentUploadField({ uhid, category, onUploaded, uploadedFileName, disabled, className }: DocumentUploadFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const pick = async (file: File) => {
    setUploading(true); setError('');
    try {
      const doc = await uploadPatientDocument(uhid, file, category);
      onUploaded(doc.id);
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    setUploading(false);
  };

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <input
          ref={inputRef} type="file" className="hidden"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) pick(f); e.target.value = ''; }}
        />
        <Button type="button" size="sm" variant="ghost" className={className} loading={uploading} disabled={disabled} onClick={() => inputRef.current?.click()}>
          📎 {uploadedFileName ? 'Replace file' : 'Attach file'}
        </Button>
        {uploadedFileName && <span className="truncate text-[12px] text-ink-muted">{uploadedFileName}</span>}
      </div>
      {error && <div className="text-[11px] text-danger-ink">{error}</div>}
    </div>
  );
}
